import React from "react";
import {ACard, ADataGridFilter} from "@atiautomacao/ati-ui-library";
import FlashOffIcon from "@mui/icons-material/FlashOff";
import {formatNumberWithMaxTwoDecimals} from "../../../../Utils/NumberUtil";
import {UnavailabilityPowerStation} from "../../../../Config/Types";

export type UnavailabilityChartMode = 'TOTAL' | 'DIN';

export const UNAVAILABILITY_CHART_TITLE = "Indisponibilidade por Usina";

/** Ícone ao lado do título do card, nas visualizações Totais e DIN. */
export const UNAVAILABILITY_CHART_TITLE_ICON: React.ComponentProps<typeof ACard>['iconTitle'] = {
    showIcon: true,
    kind: 'mui',
    iconMaterialComponent: FlashOffIcon,
    iconTitleStyle: {color: '#2D5DE7', fontSize: 24},
};

export interface UnavailabilitySeries {
    key: string;
    label: string;
    columnLabel: string;
    color: string;
    othersColor: string;
    /** Cor do texto da coluna na tabela; quando ausente, usa a cor da barra. */
    tableColor?: string;
    /** Rótulo da série no tooltip do gráfico expandido; quando ausente, usa `label`. */
    tooltipLabel?: string;
}

// As chaves correspondem ao campo "name" de cada unavailabilityItem retornado pela API.
export const CHART_SERIES: Record<UnavailabilityChartMode, UnavailabilitySeries[]> = {
    TOTAL: [
        {key: 'DIN', label: 'DIN (Disponibilidade Interna)', columnLabel: 'DIN (kWh)', color: '#2d5de7', othersColor: '#757575'},
        {key: 'DEX', label: 'DEX (Disponibilidade Externa)', columnLabel: 'DEX (kWh)', color: '#03a9f4', othersColor: '#bdbdbd'},
    ],
    DIN: [
        // A tabela mantém as cores anteriores: o azul claro das barras de Corretiva fica ilegível como texto.
        {key: 'PREVENTIVA', label: 'DIN Preventiva', columnLabel: 'DIN Preventivo (kWh)', color: '#071ac8', othersColor: '#616161', tableColor: '#1a3fa8', tooltipLabel: 'DIN Preventivo'},
        {key: 'CORRETIVA', label: 'DIN Corretiva', columnLabel: 'DIN Corretivo (kWh)', color: '#a0d1ff', othersColor: '#9e9e9e', tableColor: '#2d5de7', tooltipLabel: 'DIN Corretivo'},
        {key: 'OUTROS', label: 'Outros', columnLabel: 'Outros (kWh)', color: '#fc531f', othersColor: '#bdbdbd', tableColor: '#8fa9f5'},
    ],
};

/**
 * Ordem de empilhamento das barras, de baixo para cima. Difere da ordem de CHART_SERIES, que define
 * a legenda e as colunas da tabela: no modo DIN, Outros fica na base e Corretiva no topo.
 */
const CHART_STACK_ORDER: Record<UnavailabilityChartMode, string[]> = {
    TOTAL: ['DIN', 'DEX'],
    DIN: ['OUTROS', 'PREVENTIVA', 'CORRETIVA'],
};

export function stackedChartSeries(mode: UnavailabilityChartMode): UnavailabilitySeries[] {
    const order = CHART_STACK_ORDER[mode];
    return [...CHART_SERIES[mode]].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

export const CHART_TOOLTIP: Record<UnavailabilityChartMode, string> = {
    TOTAL: 'Mostra a perda energética por usina no período, de forma segmentada por DIN e DEX.',
    DIN: 'Mostra a perda energética DIN por usina no período, de forma segmentada por Motivo.',
};

export const OTHERS_STATION_ID = 'others';
export const OTHERS_STATION_NAME = 'Outras';

export interface StationLoss {
    id: number | typeof OTHERS_STATION_ID;
    name: string;
    values: Record<string, number>;
    total: number;
}

const emptyValues = (seriesKeys: string[]): Record<string, number> =>
    Object.fromEntries(seriesKeys.map((key) => [key, 0]));

/**
 * Soma as perdas por usina (entradas de usina, skid e equipamento já chegam somadas por usina)
 * considerando apenas as séries informadas, e ordena da maior para a menor perda total.
 */
export function aggregateUnavailabilityByStation(data: UnavailabilityPowerStation[] | undefined | null, seriesKeys: string[]): StationLoss[] {
    if (!data || data.length === 0) return [];
    const groupedMap = new Map<number, StationLoss>();

    data.forEach((item) => {
        if (!groupedMap.has(item.powerStationId)) {
            groupedMap.set(item.powerStationId, {
                id: item.powerStationId,
                name: item.powerStationName,
                values: emptyValues(seriesKeys),
                total: 0,
            });
        }
        const station = groupedMap.get(item.powerStationId)!;

        (item.unavailabilityItem ?? []).forEach((uType) => {
            if (seriesKeys.includes(uType.name)) {
                station.values[uType.name] += formatNumberWithMaxTwoDecimals(uType.value);
            }
        });
        station.total = seriesKeys.reduce((sum, key) => sum + station.values[key], 0);
    });

    return Array.from(groupedMap.values()).sort((a, b) => b.total - a.total);
}

/**
 * Regra da SGD-3413: visão simplificada mostra 19 usinas e expandida 29; as excedentes são somadas em "Outras".
 * Quando cabem todas (até 20/30), nenhuma entrada "Outras" é criada.
 */
export function limitStations(stations: StationLoss[], seriesKeys: string[], isExpansive: boolean): StationLoss[] {
    if (stations.length <= (isExpansive ? 30 : 20)) return stations;

    const limit = isExpansive ? 29 : 19;
    const remaining = stations.slice(limit);
    const others: StationLoss = {
        id: OTHERS_STATION_ID,
        name: OTHERS_STATION_NAME,
        values: emptyValues(seriesKeys),
        total: 0,
    };
    remaining.forEach((station) => {
        seriesKeys.forEach((key) => {
            others.values[key] += station.values[key];
        });
        others.total += station.total;
    });

    return [...stations.slice(0, limit), others];
}

export function hasUnavailabilityLoss(stations: StationLoss[]): boolean {
    return stations.some((station) => station.total > 0);
}

export const TOTAL_ROW_ID = 'total';
export const TOTAL_ROW_NAME = 'Total';

/**
 * Colunas da tabela detalhada das perdas DIN (visão expandida). Os mesmos rótulos são usados no arquivo
 * exportado pelo backend (sgd-main-api-service, ExportUnavailabilityLossesUseCase.DIN_LAYOUT): alterar nos dois.
 */
export const DIN_TABLE_COLUMNS: {key: string, label: string}[] = [
    {key: 'station', label: 'Usina'},
    ...CHART_SERIES.DIN.map((item) => ({key: item.key, label: item.columnLabel})),
    {key: 'total', label: 'Perda Total (kWh)'},
    {key: 'impact', label: 'Impacto (%)'},
];

export interface LossTableRow {
    id: StationLoss['id'] | typeof TOTAL_ROW_ID;
    station: string;
    values: Record<string, number>;
    total: number;
    /** Participação da Perda Total da linha na soma das Perdas Totais, de 0 a 100. */
    impact: number;
}

export interface LossTable {
    rows: LossTableRow[];
    totalRow: LossTableRow;
}

/**
 * Monta a tabela detalhada a partir das usinas já agregadas e ordenadas (sem o agrupamento "Outras"):
 * Perda Total por linha, Impacto de cada linha e a linha Total (somas e 100%).
 */
export function buildLossTable(stations: StationLoss[], seriesKeys: string[]): LossTable {
    const grandTotal = stations.reduce((sum, station) => sum + station.total, 0);
    const rows = stations.map((station) => ({
        id: station.id,
        station: station.name,
        values: station.values,
        total: station.total,
        impact: grandTotal > 0 ? (station.total / grandTotal) * 100 : 0,
    }));
    const totalValues = Object.fromEntries(
        seriesKeys.map((key) => [key, rows.reduce((sum, row) => sum + row.values[key], 0)])
    );

    return {
        rows,
        totalRow: {id: TOTAL_ROW_ID, station: TOTAL_ROW_NAME, values: totalValues, total: grandTotal, impact: 100},
    };
}

/**
 * No modo DIN o grid mostra somente registros DIN, mesmo que o usuário tenha digitado outro valor
 * no filtro da coluna DIN/DEX: o valor digitado é ignorado (a ordenação da coluna é mantida).
 */
export function applyChartModeToFilters(filters: ADataGridFilter[], mode: UnavailabilityChartMode): ADataGridFilter[] {
    if (mode !== 'DIN') return filters;
    return [
        ...filters.map((filter) => filter.name === 'type' ? {...filter, value: ''} : filter),
        {name: 'type', condition: 'equals', value: 'DIN', sort: 'none'},
    ];
}

const escapeHtml = (value: string): string =>
    value.replace(/[&<>"']/g, (char) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[char]!));

const tooltipRow = (label: string, value: string): string =>
    `<div style="display:flex;justify-content:space-between;gap:24px;line-height:22px">`
    + `<span>${label}</span><span>${value}</span></div>`;

/**
 * Tooltip da barra de uma usina no gráfico expandido em modo DIN: categorias na ordem da legenda,
 * Perda total e Impacto sobre a soma das perdas de todas as usinas (mesma regra da tabela).
 */
export function buildDinTooltipHtml(station: StationLoss, grandTotal: number): string {
    const impact = grandTotal > 0 ? (station.total / grandTotal) * 100 : 0;

    return `<div style="min-width:220px;font-size:14px;color:rgba(0,0,0,0.87)">`
        + `<div style="color:#626262;padding-bottom:8px;margin-bottom:8px;border-bottom:1px solid #e0e0e0">${escapeHtml(station.name)}</div>`
        + CHART_SERIES.DIN.map((item) => tooltipRow(item.tooltipLabel ?? item.label, String(formatNumberWithMaxTwoDecimals(station.values[item.key])))).join('')
        + tooltipRow('Perda total (kWh)', String(formatNumberWithMaxTwoDecimals(station.total)))
        + tooltipRow('Impacto (%)', `${formatNumberWithMaxTwoDecimals(Number(impact.toFixed(1)))}%`)
        + `</div>`;
}
